/**
 * Guard movement (Phase 57, S0): where a guard walks when it is not fighting.
 *
 *   patrol      — 57.8: a loop over the planet's **objective points** (uncollected
 *                 nodes, interactable ruins, resting carryables, stakes); each
 *                 waypoint is the target for two turns, then the next, the unit id
 *                 offsetting the phase so a garrison spreads out. With no objective
 *                 on the planet, a ring of four points around the unit's start.
 *   investigate — toward where the planet's guards last saw an enemy; once there,
 *                 57.8: a **search ring** around the spot, widening with the turns
 *                 since the sighting, so a guard that lost its target keeps looking
 *                 rather than standing on the last known square.
 *
 * Pure and stateless; the runner clamps the target to the movement budget.
 */

import type { PlayerState, GameState, BotSituation, Vec3 } from '../../types';
import type { BotMoveResult } from './types';
import { vecNormalize, vecAdd, vecScale, vecDot, surfaceTangentFrame } from '../../math';
import { dacos, dcos, dsin } from '../../core/dmath';

/** The surface direction `arc` radians from `from` along the tangent `dir`. */
export function surfaceStep(from: Vec3, dir: Vec3, arc: number): Vec3 {
  const n = vecNormalize(from);
  return vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(dir, dsin(arc))));
}

/** Surface directions on `planetIndex` a guard should keep an eye on, in a stable order. */
export function objectivePointsOn(state: GameState, planetIndex: number): Vec3[] {
  const pts: Array<{ key: number; dir: Vec3 }> = [];
  for (const c of state.collectibles) if (c.planetIndex === planetIndex && !c.collected) pts.push({ key: 1000 + c.id, dir: c.surfaceDir });
  for (const r of state.ruins) if (r.planetIndex === planetIndex && r.interactable) pts.push({ key: 2000 + r.id, dir: r.surfaceDir });
  for (const c of state.carryables) if (c.alive && c.planetIndex === planetIndex && c.carriedBy === null) pts.push({ key: 3000 + c.id, dir: c.surfaceDir });
  for (const f of state.plantedFlags) if (f.planetIndex === planetIndex && f.alive !== false) pts.push({ key: 4000 + f.id, dir: f.surfaceDir });
  pts.sort((a, b) => a.key - b.key);
  return pts.map(p => vecNormalize(p.dir));
}

/** This guard's place among its planet's guards (0, 1, 2 …), so a garrison spreads over the waypoints instead of bunching. */
export function guardPhase(player: PlayerState, state: GameState): number {
  const mates = state.players.filter(p => p.alive && p.teamIndex === player.teamIndex && p.planetIndex === player.planetIndex).map(p => p.id).sort((a, b) => a - b);
  return Math.max(0, mates.indexOf(player.id));
}

/** Turns a guard keeps one patrol waypoint before moving to the next (64.4: a turn-counted rule, ai/bot/durations.ts). */
export const PATROL_WAYPOINT_TURNS = 2;

/** Where a guard's patrol is headed this turn (a waypoint it may need several turns to reach). */
export function patrolTarget(player: PlayerState, state: GameState): Vec3 {
  const points = objectivePointsOn(state, player.planetIndex);
  const slot = Math.floor(state.turnNumber / PATROL_WAYPOINT_TURNS) + guardPhase(player, state);
  if (points.length >= 2) return points[slot % points.length];
  // One point: swing between it and its antipode; none: a ring of four around where the unit stands
  const { tx, ty } = surfaceTangentFrame(vecNormalize(player.surfaceDir));
  if (points.length === 1) {
    const p = points[0];
    return slot % 2 === 0 ? p : vecNormalize(vecScale(p, -1));
  }
  const bearing = (slot % 4) * (Math.PI / 2);
  const dir = vecAdd(vecScale(tx, dcos(bearing)), vecScale(ty, dsin(bearing)));
  return surfaceStep(player.surfaceDir, dir, Math.PI / 3);
}

/** A point on the ring around `centre` for the search's `step` (widening with the turns since the sighting). */
export function searchRingPoint(centre: Vec3, step: number, phase: number, planetRadius: number): Vec3 {
  const c = vecNormalize(centre);
  const { tx, ty } = surfaceTangentFrame(c);
  const bearing = (step * (Math.PI / 2)) + phase * (Math.PI / 4);
  const dir = vecAdd(vecScale(tx, dcos(bearing)), vecScale(ty, dsin(bearing)));
  const arc = Math.min(Math.PI / 2, (2 + step) / Math.max(1, planetRadius));
  return surfaceStep(c, dir, arc);
}

export function computeGuardMove(player: PlayerState, gameState: GameState, sit: BotSituation, toward: 'patrol' | 'investigate'): BotMoveResult | null {
  const planet = gameState.planets[player.planetIndex];
  if (!planet || player.movementRemaining < 0.5) return null;
  if (toward === 'investigate' && sit.alert?.lastSeenDir) {
    const seen = vecNormalize(sit.alert.lastSeenDir);
    const here = vecNormalize(player.surfaceDir);
    const arcToSeen = dacos(Math.max(-1, Math.min(1, vecDot(here, seen)))) * planet.radius;
    if (arcToSeen > 1.5) return { targetDir: seen };
    // At the spot: search around it, wider each turn since the sighting
    const since = Math.max(0, gameState.turnNumber - (sit.alert.lastSeenTurn ?? gameState.turnNumber));
    return { targetDir: searchRingPoint(seen, since, guardPhase(player, gameState), planet.radius) };
  }
  return { targetDir: patrolTarget(player, gameState) };
}
